import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment as env } from '@env';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, exhaustMap, fromEvent, map, of, startWith, switchMap, takeUntil, tap, timer } from 'rxjs';

export const POLL_MS = 15_000;
const LIMIT = 50;

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _http = inject(HttpClient);
    private readonly _url = `${env.apiUrl.replace(/\/+$/, '')}/notifications`;
    private _list = new BehaviorSubject<Notification[]>([]);
    private _unread = new BehaviorSubject<number>(0);
    /** Highest id seen. */
    private _lastId = 0;
    /** Items present at the first load never pop up. */
    private _loaded = false;
    private _polling = false;
    private _stopPolling = new Subject<void>();
    private _destroyRef = inject(DestroyRef);

    readonly notifications$ = this._list.asObservable();
    readonly unreadCount$ = this._unread.asObservable();
    /** Each unread item newer than the first load. */
    readonly newNotification$ = new Subject<Notification>();

    /** App resolver: first load, then poll while the tab is visible. */
    getAll(): Observable<Notification[]> {
        return this.sync(true).pipe(
            tap(() => this._startPolling()),
            map(() => this._list.value)
        );
    }

    /**
     * full = latest LIMIT rows (first load, panel open); else only rows after the last id seen.
     * A delta poll whose unread count doesn't add up (rows read or cleared elsewhere) falls back to a full sync,
     * so cleared notifications drop on the next poll.
     */
    sync(full = false): Observable<void> {
        const params: Record<string, number> = { limit: LIMIT };
        if (!full) params['afterId'] = this._lastId;
        return this._http.get<{ items: unknown[]; unread: number }>(this._url, { params }).pipe(
            switchMap((data) => {
                const items = data.items.map(toNotification);
                if (!full && data.unread !== this._unread.value + items.filter((n) => !n.read).length) return this.sync(true);
                this._apply(items, data.unread, full);
                return of(undefined);
            }),
            catchError((e) => {
                // signed out (or the session expired): stop, the resolver restarts polling after the next sign-in
                if (e instanceof HttpErrorResponse && e.status === 401) this._stop();
                return of(undefined);
            })
        );
    }

    private _stop(): void {
        this._stopPolling.next();
        this._polling = false;
    }

    markRead(n: Notification): void {
        if (n.read) return;
        this._update(this._list.value.map((x) => (x.id === n.id ? { ...x, read: true } : x)), this._unread.value - 1);
        this._http.patch(`${this._url}/${n.id}/read`, {}).subscribe({ error: () => {} });
    }

    delete(n: Notification): void {
        this._update(
            this._list.value.filter((x) => x.id !== n.id),
            this._unread.value - (n.read ? 0 : 1)
        );
        this._http.delete(`${this._url}/${n.id}`).subscribe({ error: () => {} });
    }

    markAllAsRead(): void {
        this._update(
            this._list.value.map((x) => ({ ...x, read: true })),
            0
        );
        this._http.post(`${this._url}/read-all`, {}).subscribe({ error: () => {} });
    }

    private _apply(items: Notification[], unread: number, full: boolean): void {
        const fresh = items.filter((n) => n.id > this._lastId);
        if (this._loaded) fresh.filter((n) => !n.read).forEach((n) => this.newNotification$.next(n));
        const list = full ? items : [...fresh, ...this._list.value];
        this._lastId = Math.max(this._lastId, ...items.map((n) => n.id));
        this._loaded = true;
        this._update(list.sort((a, b) => b.id - a.id).slice(0, LIMIT), unread);
    }

    private _update(list: Notification[], unread: number): void {
        this._list.next(list);
        this._unread.next(Math.max(0, unread));
    }

    /** Every POLL_MS while visible; paused while hidden; one poll right away on becoming visible. */
    private _startPolling(): void {
        if (this._polling) return;
        this._polling = true;
        fromEvent(document, 'visibilitychange')
            .pipe(
                map(() => !document.hidden),
                startWith(document.hidden ? false : null), // null: just loaded, wait one interval
                switchMap((visible) => (visible === false ? EMPTY : timer(visible ? 0 : POLL_MS, POLL_MS))),
                exhaustMap(() => this.sync()),
                takeUntil(this._stopPolling),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe();
    }
}

const toNotification = (n: any): Notification => ({
    id: Number(n.id),
    title: n.title,
    description: n.description,
    kind: n.kind,
    time: n.created_at,
    link: n.link,
    read: !!Number(n.read),
});
